import React from 'react';
import {InlineForm} from '../../../../components/form/Form';
import {Table} from '../../../../components/table/Table';
import {useFormSubmit} from '../../../../controllers/useSubmitForm';
import {PROJECT_COMPONENT_TYPE_ID} from '../../../common/constants/SourceConstants';
import {SplitScreen} from '../../../../components/SplitScreen';
import {
  AssetAddButton,
  ExportExcelAction,
  FeatureAssetHeaderRow,
  assetMappings,
  featureAssetColumns,
  useAssetActions,
} from './AssetUtility';
import {TabView} from '../../../../components/tab';
import {useFilter} from '../../../../controllers/useFilter';
import {TableHeader} from '../../../../components/table/Headers';
import {SearchFilter} from '../../../../components/filter/Filters';
import {componentExportParams, ComponentTable} from './ComposedComponentScreen';
import {ComposedComponentDetailForm} from './ComposedComponentForm';

const EVENT_SOURCE_ID = [
  'Feature',
  'Task',
  'ComposedComponent',
  'Design',
  'DataImport',
  'Component',
];

const ComposedComponentInlineForm = props => {
  const {
    navigation,
    route: {params},
    sourceIds,
  } = props;

  let {onSubmit} = useFormSubmit({
    eventSourceId: 'ComposedComponent',
    uri: '/projectAssetVersions',
    ...props,
  });

  const {project, feature, projectlibrary_id} = params;

  const defaultValues = {
    source: PROJECT_COMPONENT_TYPE_ID,
    project_id: project?._id,
    feature_id: feature?._id,
    is_changed: false,
    component_type: 'composed',
  };

  return (
    <InlineForm
      onSubmit={onSubmit}
      eventSourceId={'ComposedComponent'}
      submitAction="Save"
      defaultValues={defaultValues}
      layoutFields={[
        {
          label: 'Component',
          placeholder: 'Component Name',
          field: 'source_id',
          type: 'autoComplete',
          api: `/projects/${project?._id}/assets/${PROJECT_COMPONENT_TYPE_ID}/suggestions`,
          suggestionField: 'component',
          valueField: 'component',
          searchFields: ['component'],
          required: true,
          filter: {
            _id: {$nin: sourceIds},
            library_id: projectlibrary_id,
            library_version_id: null,
            component_type: 'composed',
          },
          onCreate: ({searchValue}) => {
            navigation.navigate(`add-Composed-component`, {
              ...params,
              searchValue,
            });
          },
        },
      ]}
    />
  );
};

const composedComponent = {
  type: 'text',
  field: 'component',
  header: 'Component',
};

const FeaturesComposedComponentTable = props => {
  const {
    navigation,
    route: {params},
    isRowActive,
  } = props;

  const {feature} = params;

  const tableProps = {
    api: `/features/${feature?._id}/assets/${PROJECT_COMPONENT_TYPE_ID}/asset-data`,
    filter: {component_type: 'composed'},
  };

  const {
    assetDetail,
    tasks,
    removeFromFeature,
    addDesign,
    viewDesign,
    versions,
  } = useAssetActions({
    navigation,
    params,
    source: PROJECT_COMPONENT_TYPE_ID,
    feature_id: feature?._id,
    eventSourceId: EVENT_SOURCE_ID,
  });

  const assetColumns = featureAssetColumns({
    navigation,
    params,
  });

  return (
    <Table
      isRowActive={isRowActive}
      renderHeader={headerProps => (
        <FeatureAssetHeaderRow
          feature={feature}
          assetId={PROJECT_COMPONENT_TYPE_ID}
          assetInlineForm={
            feature?.library_id ? (
              <ComposedComponentInlineForm {...props} />
            ) : (
              void 0
            )
          }
          eventSourceId={EVENT_SOURCE_ID}
          exportParams={componentExportParams({
            ...tableProps,
            fromFeature: true,
          })}
          navigation={navigation}
          showImportData={true}
          route={props.route}
          {...headerProps}
        />
      )}
      eventSourceId={EVENT_SOURCE_ID}
      selection
      onRowPress={assetDetail}
      columns={[
        composedComponent,
        assetColumns.composedComponents,
        assetColumns.designs({
          onUpload: addDesign,
          onView: viewDesign,
        }),
        assetColumns.tasks({onPress: tasks}),
        assetColumns.est_hrs,
        assetColumns.assignees,
      ]}
      moreActions={[versions, removeFromFeature]}
      {...tableProps}
    />
  );
};

const FeatureComposedComponentScreen = props => {
  return (
    <SplitScreen splitScreen={<ComposedComponentDetailForm {...props} />}>
      <FeaturesComposedComponentTable {...props} />
    </SplitScreen>
  );
};

export const AllComposedComponentScreen = props => {
  const {
    route: {params},
    api,
  } = props;

  const {project, feature, projectlibrary_id, integratedProject} = params;

  const filterProps = useFilter({});
  const {searchValue, filterValues, onChangeFilter} = filterProps;

  const sourceField = assetMappings[PROJECT_COMPONENT_TYPE_ID].sourceField;

  const tableProps = {
    api:
      api ||
      `/projects/${project?._id}/features/${feature?._id}/asset/${PROJECT_COMPONENT_TYPE_ID}/all-data`,
    addOnFilter: {
      ...filterValues.filter,
    },
    search: searchValue,
    searchFields: sourceField,
    filter: {
      library_id: projectlibrary_id,
      library_version_id: integratedProject
        ? integratedProject.library_version_id
        : null,
    },
    params: {
      show_in_development: !feature?._id,
    },
    component_type: 'composed',
  };

  const renderHeader = ({selectedIds}) => {
    return (
      <TableHeader
        actions={[
          <SearchFilter value={searchValue} onChangeFilter={onChangeFilter} />,
          <ExportExcelAction
            selectedIds={selectedIds}
            params={componentExportParams({...tableProps, fromFeature: true})}
          />,
          feature?.library_id ? (
            <AssetAddButton
              title={'Component'}
              view={'add-Composed-component'}
              params={params}
            />
          ) : (
            void 0
          ),
        ]}
      />
    );
  };

  return (
    <SplitScreen splitScreen={<ComposedComponentDetailForm {...props} />}>
      <ComponentTable
        {...props}
        renderHeader={renderHeader}
        {...tableProps}
        fromFeature
      />
    </SplitScreen>
  );
};

export const FeatureComposedComponentTab = props => {
  return (
    <TabView
      {...props}
      tabs={{
        active: {
          label: 'In Development',
          view: <FeatureComposedComponentScreen {...props} />,
        },
        all: {
          label: 'All',
          view: <AllComposedComponentScreen {...props} />,
        },
      }}
    />
  );
};
